import type { FC, ReactNode } from 'react'

import { useColorScheme } from '@dhl-ui/components'
import {
  NativeBridge,
  type NativeFrameConfig,
  type NativeFramePath,
  type AppUpdateType,
  isCurrentAppVersionAtLeast,
} from '@dhl/native-bridge'
import { dataLayerPublish } from '@dhl/datalayer'

import { useGetProfile } from '~/profile/model/profile/hooks.js'
import { paths as TrackTracePaths } from '~/tracktrace/paths.js'
import { getConfig, getTopLevelDomain } from './config/index.js'
import { rootPath } from './Routes.js'
import { links } from './links.js'

type Props = {
  children: ReactNode
}

const disableSwipeBackNavigationOnPaths = ['/dashboard', '/shipments/incoming', '/shipments/outgoing', '/more']

const {
  environment,
  native,
  features: { nativeUpdateDialog },
} = getConfig()

const prependWithRoot = (path: string) => `${rootPath}${path}`

const nativeFrame: NativeFrameConfig = {
  defaultStatusBarColor: 'none',
  paths: [
    { pattern: `/${rootPath}`, statusBar: 'brand' },
    { pattern: `/${prependWithRoot(links.incomingPiecesLink())}`, statusBar: 'brand' },
    { pattern: `/${prependWithRoot(links.moreOverviewLink())}`, statusBar: 'brand' },
    { pattern: `/${prependWithRoot(links.chatLink())}`, footer: 'subtle' },
    { pattern: `/${prependWithRoot(links.outgoingPiecesLink())}`, statusBar: 'brand' },
    { pattern: `/${prependWithRoot(links.searchResultsLink())}`, statusBar: 'brand' },
    { pattern: `/${prependWithRoot(links.locatorLink())}`, statusBar: 'brand' },
    { pattern: `/payment/*`, statusBar: 'brand' },
    { pattern: `/${prependWithRoot(links.profileOverviewLink())}`, statusBar: 'subtle', footer: 'none' },
    { pattern: `/${prependWithRoot(links.nameSettingLink())}`, footer: 'subtle' },
    { pattern: `/${prependWithRoot(links.settingsLink())}`, statusBar: 'subtle', footer: 'none' },
    { pattern: `/${prependWithRoot(links.addressesLink())}`, statusBar: 'subtle', footer: 'none' },
    { pattern: `/${prependWithRoot(links.newAddressLink())}`, footer: 'subtle' },
    {
      pattern: `/${prependWithRoot(links.editAddressLink({ addressId: '*' }))}`,
      footer: 'subtle',
    },
    { pattern: `/${prependWithRoot(links.emailAddressesLink())}`, statusBar: 'subtle', footer: 'none' },
    { pattern: `/${prependWithRoot(links.newEmailAddressLink())}`, footer: 'subtle' },
    {
      pattern: `/${prependWithRoot(links.editEmailAddressLink({ emailId: '*' }))}`,
      footer: 'subtle',
    },
    { pattern: `/${prependWithRoot(links.mobileNumberLink())}`, statusBar: 'subtle', footer: 'none' },
    { pattern: `/${prependWithRoot(links.changePasswordLink())}`, footer: 'subtle' },
    { pattern: `/${prependWithRoot(links.deleteAccountLink())}`, footer: 'subtle' },
    { pattern: `/${prependWithRoot(links.preferencesLink())}`, statusBar: 'subtle', footer: 'none' },
    {
      pattern: `/${prependWithRoot(links.deliveryPreferenceDetailsLink({ addressId: '*' }))}`,
      footer: 'none',
    },
    { pattern: `/${prependWithRoot(links.addressBookLink())}`, statusBar: 'subtle', footer: 'none' },
    { pattern: `/${prependWithRoot(links.addAddressBookLink())}`, footer: 'subtle' },
    {
      pattern: `/${prependWithRoot(links.editAddressBookLink({ addressId: '*' }))}`,
      footer: 'subtle',
    },
    { pattern: `/${prependWithRoot(links.applicationLink())}`, statusBar: 'subtle', footer: 'none' },
    { pattern: `/${prependWithRoot(links.profileSetupLink())}`, statusBar: 'subtle' },
    { pattern: `/${prependWithRoot(links.profileSetupNameLink())}`, statusBar: 'subtle' },

    ...((isCurrentAppVersionAtLeast('5.9.0')
      ? [
          { pattern: `/${prependWithRoot(links.dashboardLink())}`, statusBar: 'brand' },
          { pattern: `/${prependWithRoot(links.dashboardLink())}**`, statusBar: 'brand' },
          { pattern: `/${prependWithRoot(links.dhlExtraOverviewLink())}`, statusBar: 'subtle', footer: 'none' },
          { pattern: `/${prependWithRoot(links.dhlExtraOverviewLink())}**`, statusBar: 'subtle', footer: 'none' },
          { pattern: `/${prependWithRoot(links.c2cLink())}`, footer: 'subtle' },
          { pattern: `/${prependWithRoot(links.c2cLink())}/**`, footer: 'subtle' },
          { pattern: `/${prependWithRoot(links.trackTraceLinks())}/**`, footer: 'subtle', statusBar: 'subtle' },
          {
            pattern: `/${prependWithRoot(links.trackTraceInterventionsLink({ barcode: '*', postalCode: '*' }))}`,
            footer: 'none',
          },
          {
            pattern: `/${prependWithRoot(links.trackTraceInterventionsLink({ barcode: '*', postalCode: '*' }))}/*`,
            footer: 'subtle',
          },
          {
            pattern: `/${prependWithRoot(links.deliveryPreferenceSetupLink({ addressId: '**', type: '*' }))}`,
            statusBar: 'subtle',
            footer: 'subtle',
          },
        ]
      : [
          { pattern: `/${prependWithRoot(links.dashboardLink())}*`, statusBar: 'brand' },
          { pattern: `/${prependWithRoot(links.dhlExtraOverviewLink())}*`, statusBar: 'subtle', footer: 'none' },
          { pattern: `/${prependWithRoot(links.c2cLink())}*`, footer: 'subtle' },
          { pattern: `/${prependWithRoot(links.trackTraceLinks())}/*`, footer: 'subtle', statusBar: 'subtle' },
          {
            pattern: `/${prependWithRoot(links.deliveryPreferenceSetupLink({ addressId: '*', type: '*' }))}`,
            statusBar: 'subtle',
            footer: 'subtle',
          },
        ]) as NativeFramePath[]),
  ],
}

const ratingSettings = {
  minimumUsageCount: native.inAppReview.minimumUsageCount || 9,
  minimumInstalledDays: native.inAppReview.minimumInstalledDays || 14,
  askAgainAfterDays: native.inAppReview.askAgainAfterDays || 90,
}

export const PortalNativeBridge: FC<Props> = ({ children }) => {
  const { data: profile } = useGetProfile()
  const { preferredColorScheme, setPaletteMode } = useColorScheme()

  const handleOnLoadedUpdateDialog = (type: AppUpdateType) =>
    dataLayerPublish('IMPRESSION', {
      id: 'app-native-update-dialog',
      app_native_dialog_type: type,
    })

  const handeClickToAppStoreUpdateDialog = (type: AppUpdateType) =>
    dataLayerPublish('CLICK', {
      action: 'app-native-update-dialog-app-store',
      app_native_dialog_type: type,
    })

  const handeClickConfirmUnsupportedUpdateDialog = (type: AppUpdateType) =>
    dataLayerPublish('CLICK', {
      action: 'app-native-update-dialog-confirm-unsupported',
      app_native_dialog_type: type,
    })

  const handeClickIgnoreUpdateDialog = (type: AppUpdateType) =>
    dataLayerPublish('CLICK', {
      action: 'app-native-update-dialog-ignore',
      app_native_dialog_type: type,
    })

  return (
    <NativeBridge
      disableBackSwipeOn={disableSwipeBackNavigationOnPaths}
      fullBrightnessPaths={[
        TrackTracePaths.DELIVERY_CODE,
        TrackTracePaths.LOCKER_CODES,
        TrackTracePaths.QRCODE,
        TrackTracePaths.SP_PICKUP_CODE,
      ]}
      ratingSettings={ratingSettings}
      nativeFrame={nativeFrame}
      theme={{
        preferredColorScheme,
        setPaletteMode,
      }}
      updateDialog={{
        enable: nativeUpdateDialog.active,
        advisedAppVersion: nativeUpdateDialog.advisedNativeAppVersion,
        mandatoryAppVersion: nativeUpdateDialog.mandatoryNativeAppVersion,
        onLoaded: handleOnLoadedUpdateDialog,
        onClickToAppStore: handeClickToAppStoreUpdateDialog,
        onClickConfirmUnsupported: handeClickConfirmUnsupportedUpdateDialog,
        onClickIgnoreUpdate: handeClickIgnoreUpdateDialog,
        topLevelDomain: getTopLevelDomain(),
      }}
      userId={profile?.userId}
      isProduction={environment === 'production'}
    >
      {children}
    </NativeBridge>
  )
}
